import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type MentionVolumeOverTimeView = {
  /** `Total mentions` (with its delta) and `Daily average`. */
  kpis: KPIMetric[];
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope — an empty scope is empty even if buckets exist. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type MentionVolumeOverTimeCardProps = WidgetBlockProps & {
  view?: MentionVolumeOverTimeView;
  /** Opens the conversations behind a point (index into `rows`). */
  onViewDetails?: (index: number) => void;
  /** The help tooltip, where a page words it differently from the default. */
  helpText?: string;
  /** The title, where a page names the card differently (the executive `Conversation trend`). */
  title?: string;
};

/**
 * Mentions' first card (Figma 947:18142), and Overview's (972:19379).
 *
 * Counts, not reach: one post read by a million people is one mention here.
 * **No badge on the daily average, deliberately** — over a same-length
 * comparison period its % change is identical to the total's.
 */
export function MentionVolumeOverTimeCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
  helpText = 'Daily mentions of your brand during the selected period. Peaks show when conversation volume increased.',
  title = 'Mention volume over time',
}: MentionVolumeOverTimeCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={helpText}
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
